import { AccessibilityResultTypeConstantType } from './../../../modules/accessibility/interfaces/acc-info-interface';
import { ComplianceAccessibilityFilterParams, ComplianceAccessibilityService } from './../compliance/compliance-accessibility/compliance-accessibility.service';
import { Injectable } from '@angular/core';
import { AccessibilityGuidelinesInfoService } from '@client/app/modules/accessibility/services/mon-accessibility-guidelines/accessibility-guideline-info.service';
import { TranslateService } from '@client/app/services/translate/translate.service';

type ResultTypeOption = {
    label: string;
    value: string | null;
    leftIcon?: string;
    menuItemButtonIcons?: { monIconDirect: string }[];
};

@Injectable({
    providedIn: 'root',
})
export class AccessibilityResultTypeService {
    constructor (
        private translateService: TranslateService,
        private accessibilityGuidelinesInfoService: AccessibilityGuidelinesInfoService,
        private complianceAccessibilityService: ComplianceAccessibilityService,
    ) {}

    getCrawlIssueCount (crawl: any, abbr: string, resultType: string): number {
        const checksInError = crawl.accessibility_checks_by_result_type_and_abbr;
        if (checksInError === null && ['errors', 'errors_and_warnings'].indexOf(resultType) > -1) {
            return 0;
        }

        switch (resultType) {
            case 'errors':
                return checksInError.error[abbr] || 0;
            case 'errors_and_warnings':
                return (checksInError.error[abbr] || 0) + (checksInError.warning[abbr] || 0);
            default:
                return crawl.accessibility_checks[abbr] || 0;
        }
    }

    getResultTypeOptions (): ResultTypeOption[] {
        return [
            {
                label: this.translateService.getString('All'),
                value: null,
            },
            {
                label: this.translateService.getString('Only Errors'),
                leftIcon: 'faBug',
                value: 'errors',
            },
            {
                label: this.translateService.getString('Errors and Warnings'),
                leftIcon: 'faBug',
                menuItemButtonIcons: [{ monIconDirect: 'faBell' }],
                value: 'errors_and_warnings',
            },
        ];
    }

    getCheckCountByResultType (resultType: string, accGuideline: string): number {
        switch (resultType) {
            case 'errors':
                return this.getResultTypeCount('error', accGuideline);
            case 'errors_and_warnings':
                return this.getResultTypeCount('error', accGuideline) + this.getResultTypeCount('warning', accGuideline);
            default:
                return this.getResultTypeCount('total', accGuideline);
        }
    }

    getChecksParameters (resultType: string, accGuideline: string): string[] {
        let type: string;
        if (['errors', 'errors_and_warnings'].indexOf(resultType) > -1) {
            type = resultType;
        } else {
            type = /_v\d+$/.test(accGuideline) ? accGuideline.replace(/_v\d+$/, '') : accGuideline;
        }

        switch (type) {
            case 'errors':
                return ['error'];
            case 'errors_and_warnings':
                return ['error', 'warning'];
            case 'WCAG2-A':
                return ['A'];
            case 'WCAG2-AA':
                return ['A', 'AA'];
            case 'WCAG2-AAA':
                return ['A', 'AA', 'AAA'];
            case 'WCAG21-A':
                return ['A'];
            case 'WCAG21-AA':
                return ['A', 'AA'];
            case 'WCAG21-AAA':
                return ['A', 'AA', 'AAA'];
            case 'WCAG22-A':
                return ['A'];
            case 'WCAG22-AA':
                return ['A', 'AA'];
            case 'WCAG22-AAA':
                return ['A', 'AA', 'AAA'];
            default:
                return [];
        }
    }

    getChecksCount (checks: any[], resultType: string, abbreviations: string[], onlyErrors?: boolean): number {
        const params: ComplianceAccessibilityFilterParams = {
            only_errors: onlyErrors,
            abbreviations: abbreviations,
            result_types: [],
        };
        if (resultType === 'errors') {
            params.result_types = ['error'];
        } else if (resultType === 'errors_and_warnings') {
            params.result_types = ['error', 'warning'];
        }

        return this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(checks, params).length;
    }

    getAccessibilityChecksExcludeIgnored (checks: any[]): number {
        return checks.reduce((accumulator, check) => {
            if (!check.ignored) {
                accumulator++;
            }
            return accumulator;
        }, 0);
    }

    private getResultTypeCount (resultType: string, accGuideline: string): number {
        if (typeof accGuideline === 'undefined' || typeof accGuideline !== 'string') {
            return 0;
        }

        let result = 0;
        const abbreviations = this.getChecksParameters(resultType, accGuideline);
        const accessibilityGuidelines: Record<string, AccessibilityResultTypeConstantType> = this.accessibilityGuidelinesInfoService.retrieveSynchronousData() as Record<string, AccessibilityResultTypeConstantType>;
        const guidelineLevels = accessibilityGuidelines[accGuideline];

        if (!guidelineLevels) {
            return 0;
        }

        for (let i = 0; i < abbreviations.length; i++) {
            const levelData = guidelineLevels[abbreviations[i]];
            if (levelData) {
                const value = levelData[resultType];
                if (!isNaN(value)) {
                    result += value;
                }
            }
        }

        return result;
    }
}
